import { applyJQueryXsrf } from './xsrf';
import bootstrapModalUtils from './bootstrap-modal-utils';
import { updateSessionExpirationOnAllAjaxCompletes } from '../../common/jquery-ajax-utils';

export type DataTablesSettings = DataTables.Settings & {
	debounce?: number
};

const dataTablesPaginationPlugin: DataTables.Pagination = <DataTables.Pagination> $.fn.dataTable.ext.oPagination;
dataTablesPaginationPlugin.numbers_length = 9;

updateSessionExpirationOnAllAjaxCompletes();

/**
 * Register a "custom" pagination to dataTables with our own numbering scheme
 */
dataTablesPaginationPlugin.custom = function (page: number, pages: number) {
	const numbers = dataTablesPaginationPlugin._numbers(page, pages);

	// Prevent skip to last page when number of records is greater than 10000. This keeps OFFSET expressions in the
	// db from blowing up. We compensate by setting numbers_length to 9 to allow for more paging options.
	const limit = 1000;
	const limitedNumbers = numbers.filter(number => isNaN(Number(number)) || Number(number) < limit);

	return ['previous', limitedNumbers, 'next'];
};

function createActionButtonTemplate (actionButtonTemplate: Handlebars.TemplateDelegate, node: Node,
	rowData: any, modalTargetId?: string) {
	const $node = $(node);
	$node.html(actionButtonTemplate(rowData));

	const $link = $node.find('.command-target');
	bootstrapModalUtils.configureConfirmActionLink($link, rowData, modalTargetId);
}

function makeAjaxPostConfig (url: string, data?: any): DataTables.AjaxSettings {
	const request: DataTables.AjaxSettings = {
		url,
		type: 'POST',
		data
	};

	applyJQueryXsrf(request);

	return request;
}

// Debounce extension for server-side datatables,
// adapted from https://datatables.net/plug-ins/api/fnSetFilteringDelay, updated to work with 1.10+

function debounceSearch (element: JQuery, delay = 250) {
	const extensions = ($.fn as any).dataTableExt;
	if (extensions !== undefined && extensions.oApi.debounce === undefined) {
		extensions.oApi.debounce = function (settings: any, d: number): any {
			const self: any = this;

			self.each((i: number) => {
				extensions.iApiIndex = i;
				let timerId: any = null;
				let prevSearch: any = null;
				const control = $('input', self.fnSettings().aanFeatures.f);

				control.off('keyup search input').on('keyup search input', () => {
					if (prevSearch === null || prevSearch !== control.val()) {
						window.clearTimeout(timerId);
						prevSearch = control.val();

						timerId = window.setTimeout(() => {
							extensions.iApiIndex = i;
							self.fnFilter(control.val());
						}, d);
					}
				});

				return this;
			});

			return this;
		};
	}

	element.dataTable().debounce(delay);
}

function persistStateToSessionStorage (settings: DataTables.Settings,
	reinitialize?: () => void) {
	settings.stateSave = true;
	settings.stateDuration = -1; // session Storage
	settings.destroy = true;
	settings.drawCallback = ({ oInstance }: { oInstance: JQuery<HTMLTableElement>}) => {
		const api = oInstance.DataTable();

		const isFiltering = api.state().start > 0;
		const noData = api.data().length === 0;

		if (noData && isFiltering) {
			// tslint:disable:max-line-length

			// DataTables doesn't offer a public API to reload state. Calls to load state (_fnLoadState)
			// are only performed at the end of the plugin initialization, and the function to load state
			// is not available from within this context.

			// state reference: https://datatables.net/reference/api/state()
			// (only) call to load state during initialization:
			// https://github.com/DataTables/DataTables/blob/d975139b62e9767f18e077f3e0d9cc2babbd57f0/media/js/jquery.dataTables.js#L1303

			// in conjunction with destroy: true and a provided callback to the originating
			// initilaize method, this reinitializes the table without reloading the page

			// tslint:disable-next-line:no-console
			console.warn(`Invalid table state detected. Start index: ${api.state().start}, current page length: ${api.data().length}. Clearing state and reloading table via ${reinitialize ? 'reinitialization' : 'page reload'}.`);

			api.state.clear();
			if (reinitialize) {
				reinitialize();
			} else {
				location.reload();
			}

			// tslint:enable:max-line-length
		}
	};
}

/**
 * A DataTables Column Renderer which will allow custom HTML to be rendered in the
 * column contents. The caller is responsible for XSS sanitization. This does not affect
 * HTML generated by createdCell callback function.
 */
function dataTableRenderHtml (): DataTables.FunctionColumnRender {
	return (data: any, _type: any, _row: any, _meta: DataTables.CellMetaSettings) => data;
}

/**
 * Creates a datatable from the given jQuery Element and the supplied settings.
 * The datatable will have additional options automatically applied to it, such as
 * search debouncing, and XSS sanitization.
 * @param $dt A jQuery Element pointing to the table destined to become a datatable
 * @param settings The DataTables setting object containing the definition of the table
 * @returns The DataTables API object representing the current DataTable
 */
function createDataTable ($dt: JQuery<HTMLElement>, settings: DataTablesSettings): DataTables.Api {
	const defaults: DataTablesSettings = {
		pagingType: 'custom'
	};
	const opts = Object.assign(defaults, settings);

	// the "text" renderer will escape HTML by default, thus mitigating XSS, but
	// it has to be applied to columns or columnDefs. We need to iterate over
	// columns or build a columnDefs to do so. There is no built-in opt-out mechanism to auto
	// prevent XSS.

	// By default this will anti-xss things we don't want, such as action button templates,
	// so the callers will need to specify opt-outs, i.e., 'I know what I'm doing.'
	const textRenderer = $.fn.dataTable.render.text();

	if (opts.columns) {
		opts.columns.forEach(col => {
			if (col.render === undefined) {
				col.render = textRenderer;
			}
		});
	} else {
		throw new Error('createDataTable does not know how to work with columnDefs or DOM-defined tables. ' +
			'Either make createDataTable smarter or fully specify the DataTable via the "columns" property.');
	}

	const $dataTable = $dt.DataTable(opts);

	debounceSearch($dt, settings.debounce);

	return $dataTable;
}

export default {
	createActionButtonTemplate,
	makeAjaxPostConfig,
	persistStateToSessionStorage,
	createDataTable,
	dataTableRenderHtml
};
